import React, { useState } from "react";
import { styled, Box, Theme, useMediaQuery } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Dividend, useGetDividends } from "@rentablo/api/apiv1";
import { LoadingPlaceHolder } from "@rentablo/components";
import { AutoTable } from "@rentablo/components";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { useTableConfig } from "./useTableConfig";
import { DividendsTableHeader } from "./DividendsTableHeader";
import { DividendsMobileList } from "./DividendsMobile";

// Note: the API returns oldest-first, we want newest-first.
const dataConverter = (
  dividends: Dividend[] | undefined,
  isOriginal: boolean,
  fallbackCurrency: string
): Dividend[] =>
  [...(dividends ?? [])].reverse().map(
    (d) =>
      ({
        bookingId: d.bookingId,
        date: d.date ?? "",
        currency: isOriginal
          ? d.currency
          : (d.displayCurrency ?? fallbackCurrency),
        dividendPerShare:
          (isOriginal ? d.dividendPerShare : d.dividendPerShareAC) ?? 0,
        numberOfLots: d.numberOfLots ?? 0,
        grossAmount: (isOriginal ? d.grossAmount : d.grossAmountAC) ?? 0,
      }) as Dividend
  );

const Container = styled("div")`
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

export const DividendsTableContainer = () => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { investmentId } = useParams() as { investmentId: string };
  const parsedInvestmentId = Number.parseInt(investmentId, 10);
  const [splitAdjust, setSplitAdjust] = useState(true);
  const { selectedCurrencyView, globalCurrency } = useCurrencyDisplay();
  const isOriginal = selectedCurrencyView === "original";

  const {
    data: dividendsData,
    isLoading: isDividendsLoading,
    isPlaceholderData,
  } = useGetDividends(
    {
      id: parsedInvestmentId,
      splitAdjust,
      includeManuallyEnteredDividends: true,
      displayCurrency: isOriginal ? undefined : globalCurrency,
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const convertedDividends = dataConverter(
    dividendsData?.dividends as Dividend[],
    isOriginal,
    globalCurrency
  );
  const unreceivedDividendsCount = convertedDividends.filter(
    ({ bookingId, numberOfLots = 0 }) => !bookingId && numberOfLots > 0
  ).length;

  const dividendsTableConfig = useTableConfig();

  const noDividendsText = t(
    "domains.dividends.dividendsTable.noDividends",
    "No dividends"
  );

  if (isSm) {
    return (
      <Container>
        {isDividendsLoading ? (
          <LoadingPlaceHolder />
        ) : (
          <DividendsMobileList
            dividends={convertedDividends}
            investmentId={investmentId}
            unreceivedCount={unreceivedDividendsCount}
            splitAdjust={splitAdjust}
            onSplitAdjustChange={setSplitAdjust}
            isRefreshing={isPlaceholderData}
          />
        )}
      </Container>
    );
  }

  return (
    <Container>
      <DividendsTableHeader
        unreceivedDividendsCount={unreceivedDividendsCount}
        investmentId={investmentId}
        splitAdjust={splitAdjust}
        onToggleSplitAdjust={() => setSplitAdjust((prev) => !prev)}
      />
      {isDividendsLoading ? (
        <Box mt={6}>
          <LoadingPlaceHolder />
        </Box>
      ) : (
        <AutoTable
          settings={dividendsTableConfig}
          data={convertedDividends}
          isPending={isDividendsLoading}
          showNoData={convertedDividends.length === 0}
          noDataFirstLine={noDividendsText}
        />
      )}
    </Container>
  );
};
